import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loanFiles as seedFiles } from '../../data/loanFiles';
import type {
  DownloadStatus,
  FileType,
  LoanFile,
  PanelStatus,
  SortKey,
  SortState,
} from '../../types/files';

interface UseSmartFilesOptions {
  initialStatus: PanelStatus;
  emptyFiles: boolean;
}

export function useSmartFiles({
  initialStatus,
  emptyFiles,
}: UseSmartFilesOptions) {
  const source = emptyFiles ? [] : seedFiles;
  const [status, setStatus] = useState<PanelStatus>(initialStatus);
  const [files, setFiles] = useState<LoanFile[]>(
    initialStatus === 'ready' ? source : [],
  );
  const [query, setQuery] = useState('');
  const [activeTypes, setActiveTypes] = useState<FileType[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [sort, setSort] = useState<SortState>({
    key: 'lastModified',
    direction: 'desc',
  });
  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>('idle');
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [lastZipName, setLastZipName] = useState<string | null>(null);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    if (initialStatus !== 'loading') return;
    const timer = window.setTimeout(() => {
      setFiles(emptyFiles ? [] : seedFiles);
      setStatus('ready');
    }, 1400);
    return () => window.clearTimeout(timer);
  }, [initialStatus, emptyFiles]);

  useEffect(
    () => () => {
      timers.current.forEach((id) => window.clearTimeout(id));
      timers.current = [];
    },
    [],
  );

  const availableTypes = useMemo(() => {
    const counts = new Map<FileType, number>();
    files.forEach((file) => {
      counts.set(file.type, (counts.get(file.type) ?? 0) + 1);
    });
    return counts;
  }, [files]);

  const visibleFiles = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = files.filter((file) => {
      const matchesType =
        activeTypes.length === 0 || activeTypes.includes(file.type);
      const matchesQuery =
        needle.length === 0 ||
        file.title.toLowerCase().includes(needle) ||
        file.owner.toLowerCase().includes(needle) ||
        file.extension.toLowerCase().includes(needle);
      return matchesType && matchesQuery;
    });

    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const key: SortKey = sort.key;
      if (key === 'sizeKb') return (a.sizeKb - b.sizeKb) * factor;
      if (key === 'lastModified') {
        return (
          (new Date(a.lastModified).getTime() -
            new Date(b.lastModified).getTime()) *
          factor
        );
      }
      return String(a[key]).localeCompare(String(b[key])) * factor;
    });
  }, [files, query, activeTypes, sort]);

  const visibleIds = useMemo(
    () => visibleFiles.map((file) => file.id),
    [visibleFiles],
  );

  const selectedFiles = useMemo(
    () => files.filter((file) => selectedIds.includes(file.id)),
    [files, selectedIds],
  );

  const selectedSizeKb = useMemo(
    () => selectedFiles.reduce((total, file) => total + file.sizeKb, 0),
    [selectedFiles],
  );

  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected =
    !allVisibleSelected && visibleIds.some((id) => selectedIds.includes(id));

  const toggleType = useCallback((type: FileType) => {
    setActiveTypes((current) =>
      current.includes(type)
        ? current.filter((item) => item !== type)
        : [...current, type],
    );
  }, []);

  const clearFilters = useCallback(() => {
    setActiveTypes([]);
    setQuery('');
  }, []);

  const toggleRow = useCallback((id: string) => {
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  }, []);

  const toggleAllVisible = useCallback(() => {
    setSelectedIds((current) => {
      const everySelected =
        visibleIds.length > 0 && visibleIds.every((id) => current.includes(id));
      if (everySelected) {
        return current.filter((id) => !visibleIds.includes(id));
      }
      return Array.from(new Set([...current, ...visibleIds]));
    });
  }, [visibleIds]);

  const clearSelection = useCallback(() => setSelectedIds([]), []);

  const toggleSort = useCallback((key: SortKey) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'title' || key === 'owner' ? 'asc' : 'desc' },
    );
  }, []);

  const startZipDownload = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    setDownloadStatus('zipping');
    setDownloadProgress(8);
    const steps = [24, 46, 68, 88, 100];
    steps.forEach((value, index) => {
      const timer = window.setTimeout(
        () => setDownloadProgress(value),
        260 * (index + 1),
      );
      timers.current.push(timer);
    });
    const done = window.setTimeout(() => {
      setDownloadStatus('complete');
      setLastZipName(
        `${new Date().toISOString().slice(0, 10)}-loan-files-${ids.length}.zip`,
      );
    }, 260 * steps.length + 240);
    timers.current.push(done);
  }, []);

  const dismissDownload = useCallback(() => {
    setDownloadStatus('idle');
    setDownloadProgress(0);
  }, []);

  const deleteFiles = useCallback((ids: string[]) => {
    setFiles((current) => current.filter((file) => !ids.includes(file.id)));
    setSelectedIds((current) => current.filter((id) => !ids.includes(id)));
  }, []);

  const refresh = useCallback(() => {
    setStatus('loading');
    setSelectedIds([]);
    const timer = window.setTimeout(() => {
      setFiles(emptyFiles ? [] : seedFiles);
      setStatus('ready');
    }, 900);
    timers.current.push(timer);
  }, [emptyFiles]);

  return {
    status,
    files,
    visibleFiles,
    availableTypes,
    query,
    setQuery,
    activeTypes,
    toggleType,
    clearFilters,
    selectedIds,
    selectedFiles,
    selectedSizeKb,
    allVisibleSelected,
    someVisibleSelected,
    toggleRow,
    toggleAllVisible,
    clearSelection,
    sort,
    toggleSort,
    downloadStatus,
    downloadProgress,
    lastZipName,
    startZipDownload,
    dismissDownload,
    deleteFiles,
    refresh,
  };
}
